import moment, { Moment } from 'moment';

import { CandidateField } from '../enums';
import { BaseEnum } from './BaseEnum';
import { CareTeamCandidateField } from './CareTeamCandidateField';
import { CareTeamMemberSpeciality } from './CareTeamMemberSpeciality';
import { CareTeamMemberType } from './CareTeamMemberType';
import {
  CareTeamMemberTypeHelper,
  getTypeWithSubType,
  getTypeWithSubTypeLabel,
} from './Helpers/CareTeamMemberType';
import { make, makeArray } from './Model';
import { Phone } from './Phone';

const CandidateFieldSort = new Map<CandidateField, number>([
  [CandidateField.PHONE_NUMBER_1, 1],

  [CandidateField.PHONE_TYPE_1, 2],
  [CandidateField.PHONE_NUMBER_2, 3],
  [CandidateField.PHONE_TYPE_2, 4],
  [CandidateField.PHONE_NUMBER_3, 5],
  [CandidateField.PHONE_TYPE_3, 6],

  [CandidateField.TYPE_ROLE_TYPE, 10],
  [CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY, 11],

  [CandidateField.EMERGENCY_CONTACT, 21],
  [CandidateField.PREFERRED_CONTACT, 23],
  [CandidateField.HCP, 24],

  [CandidateField.ADDRESS_1, 31],
  [CandidateField.ADDRESS_2, 32],
  [CandidateField.CITY, 33],
  [CandidateField.STATE, 34],
  [CandidateField.ZIP_CODE, 35],
  [CandidateField.EMAIL, 36],
  [CandidateField.LIVES_WITH_MEMBER, 37],
  [CandidateField.PREFERRED_LANGUAGE, 38],
  [CandidateField.SECONDARY_LANGUAGE, 39],
]);

export class CareTeamCandidate implements CareTeamMemberTypeHelper {
  id = 0;

  firstName = '';

  lastName = '';

  fullName = '';

  phone?: Phone;

  memberId = 0;

  careTeamMemberId = 0;

  memberCommunityResourceId = 0;

  createdAt: Moment;

  deletedAt?: Moment;

  type?: CareTeamMemberType;

  aideType?: BaseEnum;

  otherRelationship?: BaseEnum;

  relationship?: BaseEnum;

  relationshipOther = '';

  specialities?: CareTeamMemberSpeciality[];

  candidateFieldValues?: CareTeamCandidateField[];

  __candidateFieldValuesSorted?: CareTeamCandidateField[];

  constructor(obj: any) {
    Object.assign(this, obj);
    this.phone = make(obj.phone, Phone);
    this.candidateFieldValues = makeArray(
      obj.candidateFieldValues,
      CareTeamCandidateField,
    );
    // eslint-disable-next-line no-underscore-dangle
    this.__candidateFieldValuesSorted = this.candidateFieldValues?.toSorted(
      (a, b) =>
        (CandidateFieldSort.get(a.field) || -1) -
        (CandidateFieldSort.get(b.field) || -1),
    );
    this.createdAt = moment.unix(obj.createdAt);
    this.deletedAt = obj.deletedAt ? moment.unix(obj.deletedAt) : undefined;
    this.type = make(obj.type, CareTeamMemberType);
    this.aideType = make(obj.aideType, BaseEnum);
    this.otherRelationship = make(obj.otherRelationship, BaseEnum);
    this.specialities = makeArray(
      obj.specialities?.map((item: BaseEnum) => ({
        specialityType: item,
        otherSpeciality: item.id?.includes(BaseEnum.OTHER)
          ? obj.subtypeOther
          : undefined,
      })),
      CareTeamMemberSpeciality,
    );
    this.relationshipOther = obj.subtypeOther;
  }

  get consolidated() {
    return !!this.careTeamMemberId || !!this.memberCommunityResourceId;
  }

  get typeWithSubType(): {
    type?: CareTeamMemberType;
    subtype?: string | undefined;
  } {
    return getTypeWithSubType(this);
  }

  get typeWithSubTypeLabel(): string {
    return getTypeWithSubTypeLabel(getTypeWithSubType(this));
  }

  get candidateFieldValuesSorted() {
    // eslint-disable-next-line no-underscore-dangle
    return this.__candidateFieldValuesSorted;
  }

  getCandidateField(searchField: CandidateField) {
    return this.candidateFieldValuesSorted?.find(
      ({ field }) => field === searchField,
    );
  }

  get candidateFieldPhones() {
    const phone1 = this.getCandidateField(CandidateField.PHONE_NUMBER_1);
    const phoneType1 = this.getCandidateField(CandidateField.PHONE_TYPE_1);
    const phone2 = this.getCandidateField(CandidateField.PHONE_NUMBER_2);
    const phoneType2 = this.getCandidateField(CandidateField.PHONE_TYPE_2);
    const phone3 = this.getCandidateField(CandidateField.PHONE_NUMBER_3);
    const phoneType3 = this.getCandidateField(CandidateField.PHONE_TYPE_3);
    return [
      phone1 || phoneType1 ? { number: phone1, type: phoneType1 } : undefined,
      phone2 || phoneType2 ? { number: phone2, type: phoneType2 } : undefined,
      phone3 || phoneType3 ? { number: phone3, type: phoneType3 } : undefined,
    ].filter(Boolean) as {
      number: CareTeamCandidateField;
      type: CareTeamCandidateField;
    }[];
  }

  get candidateFieldPhonesValues() {
    return this.candidateFieldPhones.map((item) => ({
      number: item.number?.value,
      type: item.type?.value,
    }));
  }
}

export default CareTeamCandidate;
